import type { LogService } from '@trustedshops/tswp-core-common';

/**
 * Kameleoon feature flag that drives the Upgrade Journey copy (chip, heading,
 * paragraph, button label) shown by the upgrade banner, page, and button.
 */
export const UPGRADE_JOURNEY_FLAG = 'upgrade_journey';

/**
 * Slot identifiers used as keys under a feature's `slots` map. They match the
 * component the copy is rendered in.
 */
export const UPGRADE_BANNER_SLOT = 'upgrade-banner';
export const UPGRADE_PAGE_SLOT = 'upgrade-page';
export const UPGRADE_BUTTON_SLOT = 'upgrade-button';

/**
 * Variation name of a slot's base copy: the entry under a slot's variation map
 * used when Kameleoon selects no variation (or selects `default` explicitly).
 */
export const DEFAULT_VARIATION = 'default';

/**
 * Default translation key for the chip label, used when the local content
 * config does not provide one for the resolved slot/variation.
 */
export const DEFAULT_CHIP_TRANSLATION_KEY =
  'upgrade-journey.global.premium-feature.chip';

// ---------------------------------------------------------------------------
// Kameleoon-provided variation selection. Each product feature that runs an
// experiment has its own JSON variable within UPGRADE_JOURNEY_FLAG, named
// after the `productFeatureKey` — features without experiments simply have no
// variable. It carries no translation keys — only which variation applies per
// slot. The actual keys live in the bundled default content config below.
// ---------------------------------------------------------------------------

/**
 * Variation selection for a single slot, as configured in Kameleoon.
 *
 * `variation` names an entry under the slot's variation map in the local
 * content config, or {@link DEFAULT_VARIATION} / absent to use the slot's
 * `default` entry.
 */
export interface SlotVariationConfig {
  variation?: string;
}

/**
 * Shape of a feature's JSON variable within {@link UPGRADE_JOURNEY_FLAG}: which
 * variation applies to each slot. The variable is named after the
 * `productFeatureKey`, isolating each feature's experiment config in Kameleoon.
 *
 * @example variable `FEAT_REPUTATION_MANAGER`:
 * ```json
 * {
 *   "slots": {
 *     "upgrade-banner": { "variation": "default" },
 *     "upgrade-page": { "variation": "engage_customers_v1" }
 *   }
 * }
 * ```
 */
export interface UpgradeJourneyVariationConfig {
  slots?: {
    [UPGRADE_BANNER_SLOT]?: SlotVariationConfig;
    [UPGRADE_PAGE_SLOT]?: SlotVariationConfig;
    [UPGRADE_BUTTON_SLOT]?: SlotVariationConfig;
  };
}

// ---------------------------------------------------------------------------
// Local (bundled) default content config. Holds the actual translation keys per
// slot field, keyed per slot by variation name (`default` for the base copy
// plus any named variations). Transferred from Kameleoon so copy renders even
// without consent.
// ---------------------------------------------------------------------------

/**
 * Configuration for a single slot field (e.g. `heading`, `paragraph`, `chip`,
 * `label`): the i18n key the component translates for the field.
 */
export interface SlotFieldConfig {
  translation_key: string;
}

/**
 * Per-slot field configuration. Each field is optional; an absent field falls
 * back to the component's default key (when it has one) or is reported missing.
 */
export interface UpgradeSlotConfig {
  chip?: SlotFieldConfig;
  heading?: SlotFieldConfig;
  paragraph?: SlotFieldConfig;
  label?: SlotFieldConfig;
}

/**
 * A slot's content keyed by variation name: {@link DEFAULT_VARIATION} carries
 * the base copy; any other key is a named variation Kameleoon can select.
 */
export type SlotVariationsContentConfig = Record<string, UpgradeSlotConfig>;

/**
 * A `slots` map keyed by slot identifier.
 */
export interface SlotContentConfig {
  [UPGRADE_BANNER_SLOT]?: SlotVariationsContentConfig;
  [UPGRADE_PAGE_SLOT]?: SlotVariationsContentConfig;
  [UPGRADE_BUTTON_SLOT]?: SlotVariationsContentConfig;
}

/**
 * Content for a single product feature: per-slot copy for the default variation
 * and any named variations.
 */
export interface FeatureUpgradeContent {
  slots?: SlotContentConfig;
}

/**
 * Shape of the bundled default content config: a map keyed by
 * `productFeatureKey`.
 */
export type UpgradeJourneyContentConfig = Record<string, FeatureUpgradeContent>;

/**
 * Resolves the variation name selected for a slot from the Kameleoon config.
 *
 * Returns `undefined` when there is no config for the slot (or no variable is
 * configured for the feature at all); the caller treats `undefined` and
 * {@link DEFAULT_VARIATION} identically (the slot's `default` entry).
 *
 * @param variationConfig - The feature's Kameleoon variable value.
 * @param slot - The slot being rendered.
 */
export function resolveVariationName(
  variationConfig: UpgradeJourneyVariationConfig | undefined,
  slot: string
): string | undefined {
  return variationConfig?.slots?.[slot]?.variation;
}

/**
 * Selects a slot's field config from the bundled default content config.
 *
 * Uses the slot's {@link DEFAULT_VARIATION} entry when `variationName` is
 * absent or `default`; otherwise the slot's matching named variation entry.
 * A named variation the config has no entry for (e.g. Kameleoon selects an
 * experiment this bundle does not ship copy for yet) falls back to the slot's
 * `default` entry, so the slot still renders sensible copy. Returns
 * `undefined` when the feature or slot is not present.
 *
 * @param contentConfig - The bundled default content config.
 * @param productFeatureKey - The feature whose copy is being resolved.
 * @param slot - The slot being rendered.
 * @param variationName - The resolved variation name, if any.
 */
export function selectSlotFieldConfig(
  contentConfig: UpgradeJourneyContentConfig,
  productFeatureKey: string,
  slot: string,
  variationName: string | undefined
): UpgradeSlotConfig | undefined {
  const slotVariations = contentConfig?.[productFeatureKey]?.slots?.[slot];
  if (!slotVariations) {
    return undefined;
  }

  return (
    slotVariations[variationName ?? DEFAULT_VARIATION] ??
    slotVariations[DEFAULT_VARIATION]
  );
}

/**
 * Stable, searchable token prefixing every "missing translation key" error
 * pushed to Instana. Search Instana errors for this exact string to find all
 * misconfigured Upgrade Journey slots.
 */
export const MISSING_TRANSLATION_KEY_ERROR =
  '[UpgradeJourney] Missing translation key';

/**
 * A single slot field to validate, pairing a field name with the
 * `translationKey` that was resolved for it.
 */
export interface SlotFieldResolution {
  field: string;
  translationKey: string | null;
}

/**
 * Reports an Instana error (via {@link LogService.error}) for every field that
 * resolved to **no** translation key — i.e. neither the local content config nor
 * a component default provided one — and would therefore render empty.
 *
 * Each error message starts with {@link MISSING_TRANSLATION_KEY_ERROR} and
 * carries structured `payload` details (feature, slot, field, flag) so the
 * misconfiguration can be pinpointed in Instana.
 *
 * Fields that fall back to a default key (e.g. the chip and button label) never
 * resolve to empty and are therefore never reported.
 *
 * @param logService - Logger whose `error` forwards to Instana.
 * @param logSource - The reporting component (used as the Instana log source).
 * @param productFeatureKey - The feature whose copy is being resolved.
 * @param slot - The slot being rendered (`upgrade-banner`/`-page`/`-button`).
 * @param fields - The fields to validate with their resolved keys.
 */
export function reportMissingSlotTranslationKeys(
  logService: LogService,
  logSource: string,
  productFeatureKey: string,
  slot: string,
  fields: SlotFieldResolution[]
): void {
  for (const { field, translationKey } of fields) {
    if (translationKey !== null) {
      continue;
    }

    logService.error(
      logSource,
      new Error(
        `${MISSING_TRANSLATION_KEY_ERROR}: no copy resolved for field "${field}" ` +
          `in slot "${slot}" for product feature "${productFeatureKey}". ` +
          `Configure the ${UPGRADE_JOURNEY_FLAG} default content for ` +
          `"${productFeatureKey}".slots."${slot}"."${DEFAULT_VARIATION}"` +
          `."${field}".translation_key.`
      ),
      {
        productFeatureKey,
        slot,
        field,
        flag: UPGRADE_JOURNEY_FLAG,
      }
    );
  }
}
